/**
 * Como os cinco portais resolvem DSN e `environment` do Sentry (issue #748).
 *
 * O defeito que isto fecha: quatro dos cinco apps chamavam `Sentry.init` com o DSN
 * escrito no fonte e **sem `environment`**. Sem o campo, o Sentry carimba tudo como
 * `production` — inclusive o Vite rodando em `http://localhost:3004`. Não é ruído
 * cosmético: em 2026-08-31 três erros de desenvolvimento marcados como produção
 * levaram a abrir a issue #881 ("bundle quebrado em produção, inclusive na tela de
 * login"), fechada como inválida depois de a triagem já ter cruzado os stack traces
 * com o código. Telemetria errada não polui: ela induz à conclusão errada.
 *
 * Fica aqui, e não repetido em cada `main.tsx`, por dois motivos: o DSN de fallback
 * passa a ter UMA cópia (antes eram cinco literais idênticos, que divergiriam no dia
 * em que um deles mudasse), e a regra vira função pura — testável sem montar app.
 */

/** O subconjunto de `import.meta.env` que interessa aqui. */
export type SentryEnv = {
  readonly VITE_SENTRY_DSN?: string
  readonly VITE_SENTRY_ENVIRONMENT?: string
  readonly MODE?: string
}

export type SentryRuntimeConfig = {
  readonly dsn: string
  readonly environment: string
  readonly enabled: boolean
}

/**
 * DSN do projeto `nexxus-frontend`. É um valor **público** (vai no bundle de qualquer
 * jeito — o Sentry de browser não tem segredo), então mantê-lo como fallback é seguro
 * e necessário: as `VITE_*` são resolvidas em BUILD TIME e o build do Cloudflare Pages
 * hoje não define nenhuma delas. Sem o fallback, o primeiro deploy depois desta mudança
 * desligaria a telemetria de produção inteira — e ninguém veria, porque o sintoma de
 * "Sentry desligado" é a ausência de eventos.
 */
export const SENTRY_DSN_PADRAO =
  'https://5dea9d503429f805c15a5f458e03ee72@o4508133988761600.ingest.us.sentry.io/4510839575674880'

/**
 * Usado só quando `MODE` não chega (o Vite sempre o define; sobra teste e tooling).
 * Não é `production` nem `development` de propósito: chutar `production` recria o
 * defeito da #748, e chutar `development` esconderia erro real de cliente atrás do
 * filtro que a fila do Sentry passar a usar. "Não sei" é uma resposta melhor que
 * qualquer um dos dois chutes, e aparece na fila como o sinal de config que é.
 */
export const SENTRY_ENVIRONMENT_DESCONHECIDO = 'unknown'

/**
 * Resolve o par (DSN, environment) e se o Sentry deve reportar.
 *
 * `environment` sai de `VITE_SENTRY_ENVIRONMENT || MODE`: o `MODE` do Vite já é
 * `development` no dev server e `production` no build, então o default sozinho separa
 * os dois; a env var existe para nomear um ambiente que o `MODE` não distingue
 * (staging, preview do Pages).
 *
 * Dev **continua reportando**, com `environment: development` — decisão da #748.
 * Cortar na origem (`enabled: import.meta.env.PROD`) resolveria o ruído e perderia a
 * informação; com o campo preenchido dá para filtrar no Sentry, o que era justamente
 * o que não dava antes.
 *
 * `enabled` distingue **ausente** de **vazio**: `VITE_SENTRY_DSN` não definida cai no
 * fallback (o caso do build de hoje), mas definida como string vazia desliga o envio.
 * É o único jeito de um preview conseguir rodar sem reportar — e sem essa distinção
 * `enabled` seria sempre `true`, um campo morto.
 */
export const resolveSentryConfig = (env?: SentryEnv): SentryRuntimeConfig => {
  // `typeof` e não só `=== undefined`: os cinco call sites passam `import.meta.env`,
  // cujo `ImportMetaEnv` tem index signature `any` — o compilador não garante `string`
  // aqui. Isto roda no TOPO do módulo, antes do `createRoot`, então um `.trim()` em
  // `null` não degradaria a telemetria: derrubaria o app inteiro em tela branca.
  const dsnDeclarado = env?.VITE_SENTRY_DSN
  const dsn = (typeof dsnDeclarado === 'string' ? dsnDeclarado : SENTRY_DSN_PADRAO).trim()

  const ambienteDeclarado = env?.VITE_SENTRY_ENVIRONMENT
  const modo = env?.MODE
  const environment =
    (typeof ambienteDeclarado === 'string' ? ambienteDeclarado.trim() : '') ||
    (typeof modo === 'string' ? modo.trim() : '') ||
    SENTRY_ENVIRONMENT_DESCONHECIDO

  return { dsn, environment, enabled: Boolean(dsn) }
}
